import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import PullToRefresh from '../components/PullToRefresh';
import { authAxios } from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

export default function Scorecard() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [scorecard, setScorecard] = useState([]);
  const [contestants, setContestants] = useState({});
  const [tribes, setTribes] = useState({});
  const [rosters, setRosters] = useState([]);
  const [users, setUsers] = useState({});
  const [episodes, setEpisodes] = useState([]);
  const [eventLogs, setEventLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTooltip, setActiveTooltip] = useState(null); // For mobile click support
  
  // Filters - arrays for multi-select
  const [filterPlayers, setFilterPlayers] = useState([]);
  const [filterTribes, setFilterTribes] = useState([]);
  const [filterContestants, setFilterContestants] = useState([]);
  const [filterContestantStatus, setFilterContestantStatus] = useState([]);

  useEffect(() => {
    if (currentSeason) {
      loadData();
    }
  }, [currentSeason]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Use bundle endpoint for better performance (single request instead of 6)
      const bundleRes = await authAxios.get(`${API}/seasons/${currentSeason.id}/scorecard-bundle`);
      const { scorecard: scorecardData, contestants: contestantsData, episodes: episodesData, 
              tribes: tribesData, rosters: rostersData, users: usersData } = bundleRes.data;
      
      // Load event logs separately (optional - for tooltips)
      try {
        const eventLogsRes = await authAxios.get(`${API}/seasons/${currentSeason.id}/event-log`);
        setEventLogs(eventLogsRes.data || []);
      } catch (err) {
        console.warn('Could not load event logs for tooltips:', err);
        setEventLogs([]);
      }
      
      setScorecard(scorecardData);
      setEpisodes(episodesData);
      setRosters(rostersData);
      
      // Create maps
      const contestantsMap = {};
      contestantsData.forEach(c => {
        contestantsMap[c.id] = c;
      });
      setContestants(contestantsMap);
      
      const tribesMap = {};
      tribesData.forEach(t => {
        tribesMap[t.id] = t;
      });
      setTribes(tribesMap);
      
      // Filter users to only those in the current season
      // Get user IDs from rosters (players who have contestants assigned)
      const rosterUserIds = new Set(rostersData.map(r => r.user_id));
      
      // Also get user IDs from scorecard bonus entries
      const scorecardUserIds = new Set(
        scorecardData
          .filter(s => s.contestant_id === 'BONUS')
          .map(s => s.user_id)
      );
      
      // Combine all relevant user IDs for this season
      const seasonUserIds = new Set([...rosterUserIds, ...scorecardUserIds]);
      
      // Filter users to only include those in this season
      const usersMap = {};
      usersData.forEach(u => {
        // Only include users who have season_ids including current season OR are in rosters/scorecard
        const inSeason = u.season_ids?.includes(currentSeason.id) || seasonUserIds.has(u.id);
        if (inSeason) {
          usersMap[u.id] = u;
        }
      });
      setUsers(usersMap);
      
      // Set default filter to logged-in user (only on initial load)
      if (user?.id && filterPlayers.length === 0) {
        setFilterPlayers([user.id]);
      }
    } catch (error) {
      console.error('Failed to load scorecard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to get player by user_id
  const getPlayerByUserId = (userId) => {
    return users[userId] || { id: userId, name: 'Unknown Player', color: '#64748b' };
  };
  
  // Helper to get all players for contestant (from rosters) - supports double draft
  const getPlayersForContestant = (contestantId) => {
    const contestantRosters = rosters.filter(r => r.contestant_id === contestantId);
    return contestantRosters.map(r => users[r.user_id]).filter(u => u);
  };
  
  // Helper to format player names as comma-separated string
  const formatPlayerNames = (players) => {
    if (!players || players.length === 0) return null;
    return players.map(p => p.name).join(', ');
  };
  
  // Helper to get contestant name
  const getContestantName = (contestantId) => {
    if (contestantId === 'BONUS') return 'Bonus';
    return contestants[contestantId]?.full_name || 'Unknown';
  };

  // Helper to get events for a specific contestant and episode
  const getEventsForCell = (contestantId, episodeId, userIds = []) => {
    return eventLogs.filter(event => {
      // Match contestant
      if (event.contestant_id !== contestantId) return false;
      // Match episode
      if (event.episode_id !== episodeId) return false;
      // For bonus events, match user
      if (contestantId === 'BONUS' && userIds.length > 0) {
        return userIds.includes(event.player_user_id);
      }
      return true;
    });
  };

  // Tooltip key for tracking active tooltip on mobile
  const getTooltipKey = (contestantId, episodeId, userIds) => {
    return `${contestantId}-${episodeId}-${userIds?.join(',') || ''}`;
  };

  // Create a map of existing scorecard data for quick lookup
  const scorecardMap = {};
  scorecard.forEach(entry => {
    const rowKey = `${entry.contestant_id}|${entry.user_id}`;
    if (!scorecardMap[rowKey]) {
      scorecardMap[rowKey] = {};
    }
    scorecardMap[rowKey][entry.episode_id] = entry.points;
  });
  
  // Build complete rows list including ALL contestants and ALL players for bonus
  const allRows = [];
  
  // Add rows for all contestants - for double draft, include all user_ids who own this contestant
  Object.values(contestants).forEach(contestant => {
    const contestantRosters = rosters.filter(r => r.contestant_id === contestant.id);
    
    if (contestantRosters.length === 0) {
      // Unassigned contestant - show with no user
      const rowKey = `${contestant.id}|null`;
      const episodePoints = scorecardMap[rowKey] || {};
      allRows.push({
        contestant_id: contestant.id,
        user_ids: [],
        episodePoints: episodePoints
      });
    } else {
      // For double draft: use the first owner's scorecard entry
      // (All owners have the same points per contestant - don't aggregate/double)
      const firstRoster = contestantRosters[0];
      const rowKey = `${contestant.id}|${firstRoster.user_id}`;
      const episodePoints = scorecardMap[rowKey] || {};
      
      allRows.push({
        contestant_id: contestant.id,
        user_ids: contestantRosters.map(r => r.user_id),
        episodePoints: episodePoints
      });
    }
  });
  
  // Add BONUS rows for ALL users who have bonus points OR have rosters
  // Get all user IDs that have any BONUS scorecard entries
  const bonusUserIds = new Set();
  scorecard.forEach(entry => {
    if (entry.contestant_id === 'BONUS' && entry.user_id) {
      bonusUserIds.add(entry.user_id);
    }
  });
  
  // Also include roster users (they might have 0 bonus points but should show row)
  rosters.forEach(r => {
    if (r.user_id) bonusUserIds.add(r.user_id);
  });
  
  // Add bonus row for each user with bonus points
  bonusUserIds.forEach(userId => {
    const rowKey = `BONUS|${userId}`;
    const episodePoints = scorecardMap[rowKey] || {};
    
    // Check if this user has any actual bonus points
    const hasPoints = Object.values(episodePoints).some(p => p > 0);
    
    // Always add bonus row if user has points, even if user is deleted
    // (show "Unknown Player" for deleted users)
    if (hasPoints || users[userId]) {
      allRows.push({
        contestant_id: 'BONUS',
        user_id: userId,
        episodePoints: episodePoints
      });
    }
  });
  
  // Apply filters
  const filteredRows = allRows.filter(row => {
    const contestantId = row.contestant_id;
    
    // Handle BONUS entries specially (they still have single user_id)
    if (contestantId === 'BONUS') {
      const userId = row.user_id;
      // For bonus, check player filter
      if (filterPlayers.length > 0 && !filterPlayers.includes(userId)) {
        return false;
      }
      // For bonus, check status filter - bonus entries are N/A status
      if (filterContestantStatus.length > 0 && !filterContestantStatus.includes('N/A')) {
        return false;
      }
      // Bonus entries are not affected by tribe or contestant filters
      return true;
    }
    
    const contestant = contestants[contestantId];
    if (!contestant) return false;
    
    // Filter by players (multi-select) - for double draft, check if ANY owner matches
    const userIds = row.user_ids || [];
    if (filterPlayers.length > 0 && userIds.length > 0) {
      // Show row if any of its owners are in the filter
      if (!userIds.some(uid => filterPlayers.includes(uid))) return false;
    }
    
    // Filter by tribes (multi-select)
    if (filterTribes.length > 0 && !filterTribes.includes(contestant.tribe_id)) {
      return false;
    }
    
    // Filter by contestants (multi-select)
    if (filterContestants.length > 0 && !filterContestants.includes(contestantId)) {
      return false;
    }
    
    // Filter by contestant status (multi-select)
    if (filterContestantStatus.length > 0 && !filterContestantStatus.includes(contestant.status)) {
      return false;
    }
    
    return true;
  });

  // Sort rows: Group by first player name, with contestants first, then bonus row
  const sortedRows = filteredRows.sort((a, b) => {
    // For bonus rows, use single user_id; for contestants, use first owner
    const getFirstPlayerName = (row) => {
      if (row.contestant_id === 'BONUS') {
        return getPlayerByUserId(row.user_id)?.name || 'Unassigned';
      }
      const players = getPlayersForContestant(row.contestant_id);
      return players.length > 0 ? players[0].name : 'Unassigned';
    };
    
    const playerNameA = getFirstPlayerName(a);
    const playerNameB = getFirstPlayerName(b);
    
    // First sort by player name
    if (playerNameA !== playerNameB) {
      return playerNameA.localeCompare(playerNameB);
    }
    
    // Within same player, show contestants before bonus
    if (a.contestant_id === 'BONUS' && b.contestant_id !== 'BONUS') {
      return 1; // a (bonus) comes after b (contestant)
    }
    if (a.contestant_id !== 'BONUS' && b.contestant_id === 'BONUS') {
      return -1; // a (contestant) comes before b (bonus)
    }
    
    // Both are contestants, sort by contestant name
    if (a.contestant_id !== 'BONUS' && b.contestant_id !== 'BONUS') {
      const contestantNameA = getContestantName(a.contestant_id);
      const contestantNameB = getContestantName(b.contestant_id);
      return contestantNameA.localeCompare(contestantNameB);
    }
    
    return 0;
  });
  
  // Calculate episode totals and grand total based on sorted data
  const episodeTotals = {};
  let grandTotal = 0;
  
  sortedRows.forEach(row => {
    Object.entries(row.episodePoints).forEach(([episodeId, points]) => {
      if (!episodeTotals[episodeId]) episodeTotals[episodeId] = 0;
      episodeTotals[episodeId] += points;
      grandTotal += points;
    });
  });
  
  // Helper function to clear filters
  const clearFilters = () => {
    setFilterPlayers([]);
    setFilterTribes([]);
    setFilterContestants([]);
    setFilterContestantStatus([]);
  };

  return (
    <Layout>
      <PullToRefresh onRefresh={loadData}>
        <div className="max-w-7xl mx-auto container-mobile" data-testid="scorecard">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Scorecard
          </h1>
          <p className="text-responsive text-slate-600">Season {currentSeason?.number} Points by Contestant & Episode</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="padding-card">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                  {/* Players Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Player</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterPlayers.length === 0 
                              ? 'All Players' 
                              : filterPlayers.length === 1 
                                ? users[filterPlayers[0]]?.name || 'Selected'
                                : `${filterPlayers.length} Players`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {Object.values(users).map(u => (
                          <label key={u.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterPlayers.includes(u.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (filterPlayers.includes(u.id)) {
                                  setFilterPlayers(filterPlayers.filter(id => id !== u.id));
                                } else {
                                  setFilterPlayers([...filterPlayers, u.id]);
                                }
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">
                              {u.name} {u.id === user?.id ? '(You)' : ''}
                            </span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Tribes Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Tribe</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterTribes.length === 0 
                              ? 'All Tribes' 
                              : filterTribes.length === 1 
                                ? tribes[filterTribes[0]]?.name || 'Selected'
                                : `${filterTribes.length} Tribes`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {Object.values(tribes).map(t => (
                          <label key={t.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterTribes.includes(t.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (filterTribes.includes(t.id)) {
                                  setFilterTribes(filterTribes.filter(id => id !== t.id));
                                } else {
                                  setFilterTribes([...filterTribes, t.id]);
                                }
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">{t.name}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Contestants Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Contestant</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterContestants.length === 0 
                              ? 'All Contestants' 
                              : filterContestants.length === 1 
                                ? contestants[filterContestants[0]]?.full_name || 'Selected'
                                : `${filterContestants.length} Contestants`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {Object.values(contestants).map(c => (
                          <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterContestants.includes(c.id)}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (filterContestants.includes(c.id)) {
                                  setFilterContestants(filterContestants.filter(id => id !== c.id));
                                } else {
                                  setFilterContestants([...filterContestants, c.id]);
                                }
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">{c.full_name}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Contestant Status Multi-Select Dropdown */}
                  <div>
                    <Label>Filter by Status</Label>
                    <details className="relative">
                      <summary className="list-none cursor-pointer">
                        <div className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm hover:bg-slate-50 flex items-center justify-between">
                          <span className="text-slate-700">
                            {filterContestantStatus.length === 0 
                              ? 'All Statuses' 
                              : filterContestantStatus.length === 1 
                                ? (() => {
                                    const statusLabels = {
                                      'active': 'Active',
                                      'ftc': 'Final Tribal Council',
                                      'voted_out': 'Voted Out',
                                      'removed': 'Removed',
                                      'jury': 'Jury',
                                      'winner': 'Winner',
                                      'N/A': 'N/A (Bonus)'
                                    };
                                    return statusLabels[filterContestantStatus[0]] || filterContestantStatus[0];
                                  })()
                                : `${filterContestantStatus.length} Statuses`}
                          </span>
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {[
                          { value: 'active', label: 'Active' },
                          { value: 'ftc', label: 'Final Tribal Council' },
                          { value: 'voted_out', label: 'Voted Out' },
                          { value: 'removed', label: 'Removed (Non-Vote)' },
                          { value: 'jury', label: 'Jury' },
                          { value: 'winner', label: 'Winner' },
                          { value: 'N/A', label: 'N/A (Bonus)' }
                        ].map(status => (
                          <label key={status.value} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterContestantStatus.includes(status.value)}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (filterContestantStatus.includes(status.value)) {
                                  setFilterContestantStatus(filterContestantStatus.filter(s => s !== status.value));
                                } else {
                                  setFilterContestantStatus([...filterContestantStatus, status.value]);
                                }
                              }}
                              className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                            />
                            <span className="text-sm">{status.label}</span>
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  
                  {/* Clear Filters Button */}
                  <div className="flex items-end">
                    <button
                      onClick={clearFilters}
                      disabled={filterPlayers.length === 0 && filterTribes.length === 0 && filterContestants.length === 0 && filterContestantStatus.length === 0}
                      className="w-full px-4 py-2 text-sm font-medium text-amber-600 hover:text-amber-700 border border-amber-300 rounded-md hover:bg-amber-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scorecard Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="sticky left-0 bg-slate-50 z-20">Contestant</th>
                        <th className="bg-slate-50">Player</th>
                        <th className="bg-slate-50">Tribe</th>
                        {episodes.map(ep => (
                          <th key={ep.id} className="text-center">Ep {ep.number}</th>
                        ))}
                        <th className="text-center font-bold">Total</th>
                      </tr>
                      {/* Totals Row */}
                      <tr className="bg-amber-50 font-bold">
                        <td className="sticky left-0 bg-amber-50 z-20">TOTALS</td>
                        <td className="bg-amber-50"></td>
                        <td className="bg-amber-50"></td>
                        {episodes.map(ep => (
                          <td key={ep.id} className="text-center text-amber-700">
                            {episodeTotals[ep.id] || 0}
                          </td>
                        ))}
                        <td className="text-center text-lg text-amber-700">
                          {grandTotal}
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.length === 0 ? (
                        <tr>
                          <td colSpan={episodes.length + 4} className="text-center py-8 text-slate-600">
                            No scores available
                          </td>
                        </tr>
                      ) : (
                        sortedRows.map(row => {
                          const contestantId = row.contestant_id;
                          const episodePoints = row.episodePoints;
                          
                          const contestant = contestantId === 'BONUS' ? null : contestants[contestantId];
                          const total = Object.values(episodePoints).reduce((sum, pts) => sum + pts, 0);
                          
                          // Get players: for BONUS use single userId, for contestants get all owners
                          let playerDisplay;
                          let rowKey;
                          if (contestantId === 'BONUS') {
                            const player = getPlayerByUserId(row.user_id);
                            playerDisplay = player ? (
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: player.color || '#3b82f6' }}
                                />
                                {player.name}
                              </div>
                            ) : <span className="text-slate-400">Unassigned</span>;
                            rowKey = `${contestantId}|${row.user_id}`;
                          } else {
                            const players = getPlayersForContestant(contestantId);
                            if (players.length > 0) {
                              playerDisplay = (
                                <div className="flex items-center gap-1 flex-wrap">
                                  {players.map((p, idx) => (
                                    <span key={p.id} className="flex items-center gap-1">
                                      {idx > 0 && <span className="text-slate-400">,</span>}
                                      <span 
                                        className="w-3 h-3 rounded-full flex-shrink-0 inline-block" 
                                        style={{ backgroundColor: p.color || '#3b82f6' }}
                                      />
                                      <span>{p.name}</span>
                                    </span>
                                  ))}
                                </div>
                              );
                            } else {
                              playerDisplay = <span className="text-slate-400">Unassigned</span>;
                            }
                            rowKey = `${contestantId}|${(row.user_ids || []).join('-')}`;
                          }
                          
                          const tribe = contestant ? tribes[contestant.tribe_id] : null;
                          
                          return (
                            <tr key={rowKey}>
                              <td className="sticky left-0 bg-white font-medium">
                                {getContestantName(contestantId)}
                              </td>
                              <td>
                                {playerDisplay}
                              </td>
                              <td>
                                {tribe ? (
                                  <span 
                                    className="inline-block px-3 py-1 rounded-full text-white text-sm font-medium"
                                    style={{ backgroundColor: tribe.color }}
                                  >
                                    {tribe.name}
                                  </span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              {episodes.map(ep => {
                                const points = episodePoints[ep.id];
                                const events = getEventsForCell(row.contestant_id, ep.id, row.user_ids);
                                const tooltipKey = getTooltipKey(row.contestant_id, ep.id, row.user_ids);
                                const hasEvents = events.length > 0;
                                
                                if (!points && points !== 0) {
                                  return (
                                    <td key={ep.id} className="text-center">
                                      <span className="text-slate-400">-</span>
                                    </td>
                                  );
                                }
                                
                                return (
                                  <td key={ep.id} className="text-center">
                                    <TooltipProvider delayDuration={0}>
                                      <Tooltip open={activeTooltip === tooltipKey}>
                                        <TooltipTrigger asChild>
                                          <button
                                            className={`px-2 py-1 rounded transition-colors ${
                                              hasEvents 
                                                ? 'hover:bg-amber-100 cursor-pointer text-amber-700 font-medium' 
                                                : 'cursor-default'
                                            }`}
                                            onClick={() => {
                                              if (hasEvents) {
                                                setActiveTooltip(activeTooltip === tooltipKey ? null : tooltipKey);
                                              }
                                            }}
                                            onMouseEnter={() => hasEvents && setActiveTooltip(tooltipKey)}
                                            onMouseLeave={() => setActiveTooltip(null)}
                                          >
                                            {points}
                                          </button>
                                        </TooltipTrigger>
                                        {hasEvents && (
                                          <TooltipContent 
                                            side="top" 
                                            className="bg-slate-900 text-white p-3 max-w-xs z-50"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <div className="text-xs font-semibold mb-2 text-amber-400">
                                              Episode {ep.episode_number} Events
                                            </div>
                                            <div className="space-y-1">
                                              {events.map((event, idx) => (
                                                <div key={idx} className="flex justify-between gap-4 text-sm">
                                                  <span className="text-slate-300">{event.event_name}</span>
                                                  <span className={`font-medium ${event.points >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {event.points >= 0 ? '+' : ''}{event.points}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                            <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between text-sm font-semibold">
                                              <span>Total</span>
                                              <span className="text-amber-400">{points}</span>
                                            </div>
                                          </TooltipContent>
                                        )}
                                      </Tooltip>
                                    </TooltipProvider>
                                  </td>
                                );
                              })}
                              <td className="text-center font-bold text-amber-600">
                                {total}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        </div>
      </PullToRefresh>
    </Layout>
  );
}
